import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, LogIn, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Reveal } from "@/components/site/Section";
import { http, errorMessage } from "@/lib/api";

const LoginForm = ({ onSignedIn, onForgot }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [state, setState] = useState({ loading: false, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.email || !f.password) return setState({ error: "Please enter your email and password." });
    setState({ loading: true, error: "" });
    try {
      const { data } = await http.post("/auth/login", f);
      onSignedIn(data.token);
    } catch (err) {
      setState({ loading: false, error: errorMessage(err) });
    }
  };
  return (
    <form onSubmit={submit} noValidate className="rounded-3xl border border-[#2E2E2E] bg-[#101010]/95 p-6 sm:p-8 backdrop-blur-xl" data-testid="login-form">
      <h2 className="font-display text-2xl font-bold">Sign in</h2>
      <p className="mt-2 text-sm text-[#A1A1AA]">Use the email and password you chose when you joined.</p>
      <div className="mt-6 space-y-2">
        <Label htmlFor="login-email">Email address</Label>
        <Input id="login-email" type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="lc-input" data-testid="login-email-input" />
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password">Password</Label>
          <button type="button" onClick={onForgot} className="text-sm text-[#FF6A00] hover:underline" data-testid="login-forgot-link">Forgot password?</button>
        </div>
        <Input id="login-password" type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="lc-input" data-testid="login-password-input" />
      </div>
      {state.error && <p className="mt-3 text-sm text-[#ff8a3d]" role="alert" data-testid="login-error">{state.error}</p>}
      <button type="submit" disabled={state.loading} className="lc-btn-primary mt-5 w-full" data-testid="login-submit">
        {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> Sign in</>}
      </button>
      <p className="mt-5 text-center text-sm text-[#A1A1AA]">Not a member yet? <Link to="/plans" className="text-[#FF6A00] hover:underline" data-testid="login-join-link">See our plans</Link></p>
    </form>
  );
};

const ForgotForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, error: "", info: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) return setState({ error: "Please enter a valid email address." });
    setState({ loading: true });
    try {
      const { data } = await http.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setState({ info: data.message });
    } catch (err) {
      setState({ error: errorMessage(err) });
    }
  };
  return (
    <form onSubmit={submit} noValidate className="rounded-3xl border border-[#2E2E2E] bg-[#101010]/95 p-6 sm:p-8 backdrop-blur-xl" data-testid="forgot-form">
      <h2 className="font-display text-2xl font-bold">Reset your password</h2>
      <p className="mt-2 text-sm text-[#A1A1AA]">Enter the email you joined with and we'll send you a link to choose a new password.</p>
      <div className="mt-6 space-y-2">
        <Label htmlFor="forgot-email">Email address</Label>
        <Input id="forgot-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="lc-input" data-testid="forgot-email-input" />
      </div>
      {state.error && <p className="mt-3 text-sm text-[#ff8a3d]" role="alert" data-testid="forgot-error">{state.error}</p>}
      {state.info && <p className="mt-4 flex gap-2 rounded-xl bg-[#D4A437]/10 p-3 text-sm text-[#F3E0A2]" role="status" data-testid="forgot-info"><Info className="mt-0.5 h-4 w-4 shrink-0" /> {state.info}</p>}
      <button type="submit" disabled={state.loading} className="lc-btn-primary mt-5 w-full" data-testid="forgot-submit">
        {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a reset link"}
      </button>
      <button type="button" onClick={onBack} className="mt-3 w-full text-sm text-[#A1A1AA] hover:text-[#F7F5F0]" data-testid="forgot-back">Back to sign in</button>
    </form>
  );
};

export const SignIn = ({ onSignedIn }) => {
  const [mode, setMode] = useState("login");
  return (
    <div className="relative min-h-[100svh] overflow-hidden lc-grain">
      <img src="/img/room-stove.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-[#080808]/70" aria-hidden="true" />
      <div className="relative lc-container grid min-h-[100svh] items-center gap-10 pt-28 pb-16 lg:grid-cols-2">
        <Reveal>
          <p className="lc-eyebrow">My WeeLogRun</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">Your firewood, <span className="text-[#FF6A00]">your way.</span></h1>
          <p className="mt-6 max-w-lg text-base md:text-lg text-[#A1A1AA]">Pause, change plan or cancel. Update your address. See your next delivery and payments.</p>
          <p className="mt-6 flex items-center gap-2 text-sm text-[#F7F5F0]/70"><ShieldCheck className="h-4 w-4 text-[#D4A437]" /> Your password is stored encrypted. We never see your card details.</p>
        </Reveal>
        <Reveal delay={0.1}>
          {mode === "login" ? <LoginForm onSignedIn={onSignedIn} onForgot={() => setMode("forgot")} /> : <ForgotForm onBack={() => setMode("login")} />}
        </Reveal>
      </div>
    </div>
  );
};
